import type { Recipe } from '../types/recipe';
import { compactDishKey, normalizeDishKey, type MenuDishRef } from './recipeCukcukSync';

export interface MenuCoverageItem extends MenuDishRef {
  type?: string;
}

export interface MissingRecipeItem {
  code: string;
  name: string;
  group?: string;
  type?: string;
}

export interface OrphanRecipeItem {
  id?: string;
  itemId: string;
  itemName: string;
  group?: string;
  ingredientCount: number;
}

/** Option / biến thể menu không cần recipe riêng (nóng/đá option, đổi hạt, shot, ly…). */
export const isOptionLikeMenuItem = (dish: MenuCoverageItem | MenuDishRef): boolean => {
  const type = ((dish as MenuCoverageItem).type || '').trim().toLowerCase();
  if (type === 'option') return true;

  const code = (dish.code || '').trim().toUpperCase();
  if (code.startsWith('STPV-') || code.startsWith('OPT-')) return true;

  // CUKCUK option rows often have empty type; group still identifies them.
  if (!type) {
    const group = normalizeDishKey(dish.group || '');
    const optionGroups = [
      'nhiet do',
      'them shot',
      'doi hat',
      'doi loai hat',
      'loai ly',
      'phuc vu',
      'so thich phuc vu',
      'trong luong',
    ];
    if (optionGroups.some((g) => group === g || group.includes(g))) return true;
  }

  return false;
};

const buildRecipeIndexes = (recipes: Recipe[]) => {
  const byCode = new Map<string, Recipe>();
  const byName = new Map<string, Recipe>();

  recipes.forEach((recipe) => {
    const code = (recipe.itemId || '').trim().toLowerCase();
    if (code) byCode.set(code, recipe);
    (recipe.itemIdAliases || []).forEach((alias) => {
      const aliasCode = alias.trim().toLowerCase();
      if (aliasCode && !byCode.has(aliasCode)) byCode.set(aliasCode, recipe);
    });

    const nameKey = normalizeDishKey(recipe.itemName);
    if (nameKey && !byName.has(nameKey)) byName.set(nameKey, recipe);

    const compact = compactDishKey(recipe.itemName);
    if (compact && !byName.has(`c:${compact}`)) byName.set(`c:${compact}`, recipe);
  });

  return { byCode, byName };
};

export const recipeCoversMenuDish = (
  dish: MenuDishRef,
  recipes: Recipe[],
): { covered: boolean; recipe?: Recipe; method?: string } => {
  const { byCode, byName } = buildRecipeIndexes(recipes);
  const code = dish.code.trim();

  if (code && byCode.has(code.toLowerCase())) {
    return { covered: true, recipe: byCode.get(code.toLowerCase()), method: 'code' };
  }

  const nameKey = normalizeDishKey(dish.name);
  if (nameKey && byName.has(nameKey)) {
    return { covered: true, recipe: byName.get(nameKey), method: 'name' };
  }

  const compact = compactDishKey(dish.name);
  if (compact && byName.has(`c:${compact}`)) {
    return { covered: true, recipe: byName.get(`c:${compact}`), method: 'compact' };
  }

  return { covered: false };
};

/** Thực đơn CUKCUK chưa có công thức (cần thêm). Option/STPV không tính. */
export const findMenuMissingRecipes = (
  menu: MenuDishRef[],
  recipes: Recipe[],
): MissingRecipeItem[] =>
  menu
    .filter((dish) => !!dish.code?.trim() && !!dish.name?.trim())
    .filter((dish) => !isOptionLikeMenuItem(dish))
    .filter((dish) => !recipeCoversMenuDish(dish, recipes).covered)
    .map((dish) => ({
      code: dish.code.trim(),
      name: dish.name.trim(),
      group: dish.group,
      type: (dish as MenuCoverageItem).type,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'vi'));

/** Công thức không còn trên thực đơn CUKCUK (có thể bỏ qua). */
export const findOrphanRecipesNotOnMenu = (
  menu: MenuDishRef[],
  recipes: Recipe[],
): OrphanRecipeItem[] => {
  const menuCodes = new Set(menu.map((d) => d.code.trim().toLowerCase()).filter(Boolean));
  const menuNames = new Set(menu.map((d) => normalizeDishKey(d.name)).filter(Boolean));
  const menuCompacts = new Set(menu.map((d) => compactDishKey(d.name)).filter(Boolean));

  return recipes
    .filter((recipe) => {
      const code = (recipe.itemId || '').trim().toLowerCase();
      if (code && menuCodes.has(code)) return false;
      if ((recipe.itemIdAliases || []).some((alias) => menuCodes.has(alias.trim().toLowerCase()))) {
        return false;
      }

      const nameKey = normalizeDishKey(recipe.itemName);
      if (nameKey && menuNames.has(nameKey)) return false;

      const compact = compactDishKey(recipe.itemName);
      if (compact && menuCompacts.has(compact)) return false;

      return true;
    })
    .map((recipe) => ({
      id: recipe.id,
      itemId: recipe.itemId,
      itemName: recipe.itemName,
      group: recipe.group,
      ingredientCount: recipe.ingredients?.length || 0,
    }))
    .sort((a, b) => a.itemName.localeCompare(b.itemName, 'vi'));
};
